import React, { useState } from 'react';
import { 
  BoltIcon, 
  ExclamationTriangleIcon, 
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { riskApi } from '@/services/api';

interface EmergencyControlsProps {
  activeStops: any[];
  onEmergencyStop: () => void;
  onLiftStop: () => void;
}

const EmergencyControls: React.FC<EmergencyControlsProps> = ({
  activeStops,
  onEmergencyStop,
  onLiftStop
}) => {
  const [isTriggering, setIsTriggering] = useState(false);
  const [isLifting, setIsLifting] = useState(false);
  const [showTriggerDialog, setShowTriggerDialog] = useState(false);
  const [showLiftDialog, setShowLiftDialog] = useState(false);
  const [selectedStop, setSelectedStop] = useState<any>(null);
  const [triggerReason, setTriggerReason] = useState('');
  const [liftReason, setLiftReason] = useState('');
  const [triggerScope, setTriggerScope] = useState('all');
  const [error, setError] = useState<string | null>(null);

  const handleTriggerEmergencyStop = async () => {
    if (!triggerReason.trim()) {
      setError('Please provide a reason for the emergency stop');
      return;
    }

    try {
      setIsTriggering(true);
      setError(null);

      await riskApi.triggerEmergencyStop({
        reason: triggerReason,
        scope: triggerScope
      });

      setShowTriggerDialog(false);
      setTriggerReason('');
      setTriggerScope('all');
      onEmergencyStop();

    } catch (err: any) {
      console.error('Failed to trigger emergency stop:', err);
      setError(err.message || 'Failed to trigger emergency stop');
    } finally {
      setIsTriggering(false);
    }
  };

  const handleLiftEmergencyStop = async () => {
    if (!selectedStop || !liftReason.trim()) {
      setError('Please provide a reason for lifting the emergency stop');
      return;
    }

    try {
      setIsLifting(true);
      setError(null);

      await riskApi.liftEmergencyStop(selectedStop.id, liftReason);

      setShowLiftDialog(false);
      setLiftReason('');
      setSelectedStop(null);
      onLiftStop();

    } catch (err: any) {
      console.error('Failed to lift emergency stop:', err);
      setError(err.message || 'Failed to lift emergency stop');
    } finally {
      setIsLifting(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="card shadow rounded-lg">
      <div className="px-6 py-4 border-b border-primary-custom">
        <h3 className="text-heading-3 font-medium text-primary-custom">Emergency Controls</h3>
        <p className="text-body text-secondary-custom">Stop all trading activities immediately</p>
      </div>

      <div className="px-6 py-4 space-y-4">
        {/* Active Emergency Stops */}
        {activeStops.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-body font-medium text-error-custom">Active Emergency Stops</h4>
            {activeStops.map((stop) => (
              <div key={stop.id} className="bg-error-subtle border border-error-subtle rounded-md p-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <BoltIcon className="h-4 w-4 text-red-500 mr-2" />
                      <span className="text-body font-medium text-error-custom">
                        {stop.reason}
                      </span>
                    </div>
                    <div className="mt-1 text-caption text-error-custom">
                      Triggered: {formatDateTime(stop.created_at)}
                    </div>
                    <div className="mt-1 text-caption text-error-custom">
                      Scope: {stop.scope} • Type: {stop.stop_type}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedStop(stop);
                      setShowLiftDialog(true);
                    }}
                    className="ml-3 inline-flex items-center px-2 py-1 border border-red-300 rounded text-caption font-medium text-red-700 bg-red-100 hover:bg-red-200"
                  >
                    <CheckCircleIcon className="h-3 w-3 mr-1" />
                    Lift
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Trigger Emergency Stop Button */}
        <div className="pt-4 border-t border-primary-custom">
          <button
            onClick={() => setShowTriggerDialog(true)}
            disabled={activeStops.length > 0}
            className={`w-full inline-flex items-center justify-center px-4 py-3 border border-transparent rounded-md shadow-sm text-body font-medium text-white ${
              activeStops.length > 0
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700 focus:ring-2 focus:ring-red-500'
            }`}
          >
            <BoltIcon className="h-5 w-5 mr-2" />
            {activeStops.length > 0 ? 'Emergency Stop Active' : 'Trigger Emergency Stop'}
          </button>
          {activeStops.length > 0 && (
            <p className="mt-2 text-caption text-secondary-custom text-center">
              Lift active emergency stops before triggering a new one
            </p>
          )}
        </div>

        {/* System Status */}
        <div className="bg-secondary-custom rounded-md p-3">
          <div className="flex items-center">
            {activeStops.length > 0 ? (
              <>
                <XCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                <span className="text-body text-error-custom font-medium">Trading Halted</span>
              </>
            ) : (
              <>
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                <span className="text-body text-success-custom font-medium">System Operational</span>
              </>
            )}
          </div>
          <p className="text-caption text-secondary-custom mt-1">
            {activeStops.length > 0 
              ? 'All trading activities are stopped'
              : 'Risk management system is monitoring'
            }
          </p>
        </div>
      </div>

      {/* Trigger Emergency Stop Dialog */}
      {showTriggerDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-secondary-custom w-96 shadow-lg rounded-md card">
            <div className="mt-3">
              <div className="flex items-center mb-4">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-500 mr-3" />
                <h3 className="text-heading-3 font-medium text-primary-custom">
                  Trigger Emergency Stop
                </h3>
              </div>

              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-body text-red-600">{error}</p>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-body font-medium text-secondary-custom mb-2">
                  Reason (Required)
                </label>
                <textarea
                  value={triggerReason}
                  onChange={(e) => setTriggerReason(e.target.value)}
                  className="form-input form-bg-custom form-border-custom form-text-custom form-placeholder-custom focus-border-custom focus-ring-custom w-full rounded-md"
                  rows={3}
                  placeholder="Explain why you're triggering an emergency stop..."
                />
              </div>

              <div className="mb-6">
                <label className="block text-body font-medium text-secondary-custom mb-2">
                  Scope
                </label>
                <select
                  value={triggerScope}
                  onChange={(e) => setTriggerScope(e.target.value)}
                  className="form-input form-bg-custom form-border-custom form-text-custom focus-border-custom focus-ring-custom w-full rounded-md"
                >
                  <option value="all">All Trading Activities</option>
                  <option value="strategies">Active Strategies Only</option>
                  <option value="automation">Automation Sessions Only</option>
                </select>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowTriggerDialog(false);
                    setTriggerReason('');
                    setError(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-body font-medium text-gray-700 hover:bg-gray-50"
                  disabled={isTriggering}
                >
                  Cancel
                </button>
                <button
                  onClick={handleTriggerEmergencyStop}
                  disabled={isTriggering || !triggerReason.trim()}
                  className="flex-1 px-4 py-2 bg-red-600 border border-transparent rounded-md text-body font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {isTriggering ? 'Triggering...' : 'Emergency Stop'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lift Emergency Stop Dialog */}
      {showLiftDialog && selectedStop && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-secondary-custom w-96 shadow-lg rounded-md card">
            <div className="mt-3">
              <div className="flex items-center mb-4">
                <CheckCircleIcon className="h-8 w-8 text-green-500 mr-3" />
                <h3 className="text-heading-3 font-medium text-primary-custom">
                  Lift Emergency Stop
                </h3>
              </div>

              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-body text-red-600">{error}</p>
                </div>
              )}

              <div className="mb-4 bg-secondary-custom rounded-md p-3">
                <p className="text-body text-secondary-custom">
                  <strong>Current Stop:</strong> {selectedStop.reason}
                </p>
                <p className="text-caption text-tertiary-custom mt-1">
                  Triggered: {formatDateTime(selectedStop.created_at)}
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-body font-medium text-secondary-custom mb-2">
                  Reason for Lifting (Required)
                </label>
                <textarea
                  value={liftReason}
                  onChange={(e) => setLiftReason(e.target.value)}
                  className="form-input form-bg-custom form-border-custom form-text-custom form-placeholder-custom focus-border-custom focus-ring-custom w-full rounded-md"
                  rows={3}
                  placeholder="Explain why it's safe to resume trading..."
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowLiftDialog(false);
                    setLiftReason('');
                    setSelectedStop(null);
                    setError(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-body font-medium text-gray-700 hover:bg-gray-50"
                  disabled={isLifting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleLiftEmergencyStop}
                  disabled={isLifting || !liftReason.trim()}
                  className="flex-1 px-4 py-2 bg-green-600 border border-transparent rounded-md text-body font-medium text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {isLifting ? 'Lifting...' : 'Lift Emergency Stop'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmergencyControls; 